import { useState } from 'react';
import { Send } from 'lucide-react';
import type { TaskUpstreamPeople, UpstreamPerson } from '@shared/types/task-sources';
import type { UpstreamProposal } from '@shared/upstream-deferral';
import { useLoader, type LoadFn } from '@/hooks/use-loader';
import type { ApiResponse } from '@/lib/api';
import { describeApiError } from '@/lib/api-errors';
import { formatPercent } from '@/lib/formatters';
import { ComboboxField } from '@/components/ComboboxField';
import { LoadError } from '@/components/LoadError';
import { LoadingState } from '@/components/LoadingState';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

/** One item the picker lists: its number in the round and what it says. */
export interface SendUpstreamItem {
  number: number;
  title: string;
}

/**
 * SF-T-101: who a send can tag, read when the dialog opens (`load`, cached under `key`): the task's
 * creator on the source and the board's people.
 */
export interface UpstreamTagging {
  key: string;
  load: LoadFn<ApiResponse<TaskUpstreamPeople>>;
}

/**
 * T-58: "Send to <source>" on the AI builder's questions or suggestions on a mirrored task. Opens a
 * picker with a tick box per item that can still go (all ticked to start); Send posts the ticked ones
 * there as a comment, and resolves true when they went. SF-T-101: with `tagging`, the comment tags the
 * task's creator there (a tick box, on to start), or someone else picked from the board's people.
 */
export function SendUpstreamButton({
  sourceLabel,
  items,
  what,
  tagging,
  onSend,
}: {
  /** "Asana". */
  sourceLabel: string;
  /** The open items not sent yet. */
  items: SendUpstreamItem[];
  /** "questions" or "suggestions", for the dialog's words. */
  what: 'questions' | 'suggestions';
  tagging?: UpstreamTagging;
  /** `mentionId`: who to tag on the source, by their account id there; null for nobody. */
  onSend: (numbers: number[], mentionId: string | null) => Promise<boolean>;
}) {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [sending, setSending] = useState(false);
  // SF-T-101: whether to tag someone (on to start), and who when it isn't the creator.
  const [tagOn, setTagOn] = useState(true);
  const [tagId, setTagId] = useState<string | null>(null);
  const peopleLoader = useLoader((signal) => tagging!.load(signal), [tagging?.key], { key: tagging?.key, enabled: open && Boolean(tagging) });
  const tagPeople = peopleLoader.data?.data ?? null;
  const tagged: UpstreamPerson | null = tagId ? (tagPeople?.people.find((p) => p.id === tagId) ?? null) : (tagPeople?.creator ?? null);
  const mentionId = tagging && tagOn && tagged ? tagged.id : null;
  const tagLoading = Boolean(tagging) && peopleLoader.loading && !tagPeople;
  // Only items still listed: one sent or decided meanwhile (a live update) drops out of the picks.
  const chosen = items.filter((i) => picked.has(i.number)).map((i) => i.number);
  if (!items.length) return null;

  const start = () => {
    setPicked(new Set(items.map((i) => i.number)));
    setTagOn(true);
    setTagId(null);
    setOpen(true);
  };
  const toggle = (number: number, on: boolean) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (on) next.add(number);
      else next.delete(number);
      return next;
    });
  const send = async () => {
    if (!chosen.length || tagLoading) return;
    setSending(true);
    const ok = await onSend(chosen, mentionId);
    setSending(false);
    if (ok) setOpen(false);
  };

  return (
    <>
      {/* h-11 on a phone (the task pop-up is a phone page): a 44px touch target. */}
      <Button variant="outline" size="sm" className="h-11 sm:h-8" onClick={start} data-hint={`Ask the people on ${sourceLabel} to decide them`}>
        <Send aria-hidden />
        Send to {sourceLabel}
      </Button>
      <Dialog open={open} onOpenChange={(next) => !sending && setOpen(next)}>
        <DialogContent className="sm:max-w-md">
          <form
            noValidate
            className="contents"
            onSubmit={(e) => {
              e.preventDefault();
              void send();
            }}
          >
            <DialogHeader>
              <DialogTitle>Send to {sourceLabel}</DialogTitle>
              <DialogDescription>
                The ticked {what} go to the task on {sourceLabel} as a comment from you. The task waits on {sourceLabel} until someone
                there replies.
              </DialogDescription>
            </DialogHeader>
            <div role="group" aria-label={`The ${what} to send`} className="max-h-[50vh] space-y-2 overflow-y-auto">
              {items.map((item) => (
                <label key={item.number} className="flex min-h-11 items-start gap-2 py-1 text-sm sm:min-h-0 sm:py-0">
                  <Checkbox className="mt-0.5" checked={picked.has(item.number)} disabled={sending} onCheckedChange={(on) => toggle(item.number, on === true)} />
                  <span className="min-w-0">
                    <span className="text-muted-foreground">{item.number}. </span>
                    {item.title}
                  </span>
                </label>
              ))}
            </div>
            {tagging ? (
              <TagPicker
                sourceLabel={sourceLabel}
                loading={tagLoading}
                loadError={peopleLoader.data?.error ? describeApiError(peopleLoader.data) : null}
                onRetry={peopleLoader.reload}
                people={tagPeople}
                tagged={tagged}
                on={tagOn}
                disabled={sending}
                onToggle={setTagOn}
                onPick={(id) => {
                  setTagId(id);
                  setTagOn(true);
                }}
              />
            ) : null}
            <DialogFooter>
              <Button type="button" variant="outline" className="h-11 sm:h-9" disabled={sending} onClick={() => setOpen(false)}>
                Cancel
              </Button>
              {/* Not while who to tag is loading: a send then would leave out the creator the dialog says it tags. */}
              <Button type="submit" className="h-11 sm:h-9" disabled={!chosen.length || tagLoading} loading={sending}>
                Send {chosen.length === items.length ? '' : `${chosen.length} `}to {sourceLabel}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * SF-T-201: "Send to <source>" from a comment's ⋯ menu on a mirrored task: any comment made here (yours,
 * someone else's, a coding agent's screenshots, a run's report). It goes up as it is now, with its files and
 * without the AI builder's blocks, signed with who wrote it and that you sent it. With `tagging`, the comment
 * tags the task's creator there (on to start), or someone else from the board's people. The task's Waiting on
 * doesn't change. `onSend` resolves true when it went; the dialog closes then.
 */
export function SendCommentUpstreamDialog({
  open,
  onOpenChange,
  sourceLabel,
  author,
  mine = false,
  files = 0,
  tagging,
  onSend,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** "Asana". */
  sourceLabel: string;
  /** Who wrote it, as it's signed there: "Kate", or "AI Agent" for an agent's or a run's. */
  author: string;
  /** Your own comment: signed with just your name. */
  mine?: boolean;
  /** How many files it carries, for the dialog's words. */
  files?: number;
  tagging?: UpstreamTagging;
  /** `mentionId`: who to tag on the source, by their account id there; null for nobody. */
  onSend: (mentionId: string | null) => Promise<boolean>;
}) {
  // Mounted only while open, so each opening starts from the creator, ticked.
  const [sending, setSending] = useState(false);
  return (
    <Dialog open={open} onOpenChange={(next) => !sending && onOpenChange(next)}>
      <DialogContent className="sm:max-w-md">
        {open ? (
          <SendCommentForm
            sourceLabel={sourceLabel}
            author={author}
            mine={mine}
            files={files}
            tagging={tagging}
            sending={sending}
            onSend={async (mentionId) => {
              setSending(true);
              const ok = await onSend(mentionId);
              setSending(false);
              if (ok) onOpenChange(false);
            }}
            onCancel={() => onOpenChange(false)}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function SendCommentForm({
  sourceLabel,
  author,
  mine,
  files,
  tagging,
  sending,
  onSend,
  onCancel,
}: {
  sourceLabel: string;
  author: string;
  mine: boolean;
  files: number;
  tagging?: UpstreamTagging;
  sending: boolean;
  onSend: (mentionId: string | null) => Promise<void>;
  onCancel: () => void;
}) {
  const [tagOn, setTagOn] = useState(true);
  const [tagId, setTagId] = useState<string | null>(null);
  const peopleLoader = useLoader((signal) => tagging!.load(signal), [tagging?.key], { key: tagging?.key, enabled: Boolean(tagging) });
  const tagPeople = peopleLoader.data?.data ?? null;
  const tagged: UpstreamPerson | null = tagId ? (tagPeople?.people.find((p) => p.id === tagId) ?? null) : (tagPeople?.creator ?? null);
  const mentionId = tagging && tagOn && tagged ? tagged.id : null;
  const tagLoading = Boolean(tagging) && peopleLoader.loading && !tagPeople;
  const withFiles = files === 1 ? ', with its file,' : files > 1 ? `, with its ${files} files,` : '';

  return (
    <form
      noValidate
      className="contents"
      onSubmit={(e) => {
        e.preventDefault();
        if (!tagLoading && !sending) void onSend(mentionId);
      }}
    >
      <DialogHeader>
        <DialogTitle>Send to {sourceLabel}</DialogTitle>
        <DialogDescription>
          This comment{withFiles} goes to the task on {sourceLabel} as it is now, signed {mine ? 'with your name' : `“${author}, sent by you”`}. The AI
          builder’s questions and suggestions in it stay here. The task’s Waiting on doesn’t change.
        </DialogDescription>
      </DialogHeader>
      {tagging ? (
        <TagPicker
          sourceLabel={sourceLabel}
          loading={tagLoading}
          loadError={peopleLoader.data?.error ? describeApiError(peopleLoader.data) : null}
          onRetry={peopleLoader.reload}
          people={tagPeople}
          tagged={tagged}
          on={tagOn}
          disabled={sending}
          onToggle={setTagOn}
          onPick={(id) => {
            setTagId(id);
            setTagOn(true);
          }}
        />
      ) : null}
      <DialogFooter>
        <Button type="button" variant="outline" className="h-11 sm:h-9" disabled={sending} onClick={onCancel}>
          Cancel
        </Button>
        {/* Not while who to tag is loading: a send then would leave out the creator the dialog says it tags. */}
        <Button type="submit" className="h-11 sm:h-9" disabled={tagLoading} loading={sending}>
          <Send aria-hidden />
          Send to {sourceLabel}
        </Button>
      </DialogFooter>
    </form>
  );
}

/**
 * SF-T-101: the dialog's tag: a tick box for the person the comment tags (the task's creator there to
 * start), and a search of the board's people to tag someone else instead.
 */
function TagPicker({
  sourceLabel,
  loading,
  loadError,
  people,
  tagged,
  on,
  disabled,
  onToggle,
  onPick,
  onRetry,
}: {
  sourceLabel: string;
  loading: boolean;
  loadError: string | null;
  onRetry: () => void;
  people: TaskUpstreamPeople | null;
  tagged: UpstreamPerson | null;
  on: boolean;
  disabled: boolean;
  onToggle: (on: boolean) => void;
  onPick: (id: string) => void;
}) {
  if (loading) return <LoadingState inline label={`Finding who created it on ${sourceLabel}…`} />;
  if (loadError) return <LoadError compact title={`Couldn't read who to tag on ${sourceLabel}; a send now tags nobody`} error={loadError} onRetry={onRetry} />;
  const creatorId = people?.creator?.id;
  const options = (people?.people ?? []).map((p) => ({ value: p.id, label: p.id === creatorId ? `${p.name} (created it)` : p.name }));
  return (
    <div className="space-y-2 border-t pt-3">
      <label className="flex min-h-11 items-center gap-2 text-sm sm:min-h-0">
        <Checkbox checked={on && Boolean(tagged)} disabled={disabled || !tagged} onCheckedChange={(next) => onToggle(next === true)} />
        <span className="min-w-0">
          {tagged ? `Tag ${tagged.name} on ${sourceLabel}` : `Tag someone on ${sourceLabel}`}
          {tagged && tagged.id === creatorId ? <span className="text-muted-foreground"> (created the task)</span> : null}
        </span>
      </label>
      {options.length ? (
        <ComboboxField
          id="send-upstream-tag"
          label={tagged ? 'Or tag someone else' : 'Who to tag'}
          labelClassName="text-xs text-muted-foreground"
          size="sm"
          value={tagged?.id ?? ''}
          displayValue={tagged?.name ?? ''}
          options={options}
          placeholder="Pick someone"
          searchPlaceholder={`Search the ${sourceLabel} board's people`}
          emptyText="Nobody by that name"
          disabled={disabled}
          onSelect={onPick}
        />
      ) : null}
      {people?.error ? <p className="text-xs text-muted-foreground">Couldn’t read everything from {sourceLabel}: {people.error}.</p> : null}
      {!tagged && !options.length && !people?.error ? <p className="text-xs text-muted-foreground">{sourceLabel} didn’t say who created it, so nobody is tagged.</p> : null}
    </div>
  );
}

/**
 * T-58: under an item sent upstream: waiting for a reply there (`waiting`: the task still waits on its
 * source), only sent (a reply came back without an answer for it, so it's for a person here), or what
 * Jev read in the reply (the answer it picked is pre-selected above), with whose reply and how sure.
 */
export function SentUpstreamNote({
  sourceLabel,
  proposal,
  waiting = true,
  children,
}: {
  sourceLabel: string;
  proposal?: UpstreamProposal;
  waiting?: boolean;
  children?: string;
}) {
  if (!proposal) {
    return (
      <StatusBadge tone={waiting ? 'info' : 'neutral'} size="sm" dot>
        {waiting ? `Sent to ${sourceLabel}, waiting for a reply` : `Sent to ${sourceLabel}; no answer read from the reply`}
      </StatusBadge>
    );
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
      <StatusBadge tone="success" size="sm" dot>
        {sourceLabel} replied
      </StatusBadge>
      <span>
        {children ?? proposal.choice}
        {proposal.reply_author ? `, from ${proposal.reply_author}’s reply` : ''} · {formatPercent(proposal.confidence, 'score')} sure
      </span>
    </span>
  );
}
